import { useState, useCallback, useEffect } from "react";
import {
  Spinner, Database, Check, X, CaretDown, CaretUp, Trash,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { getResearchCache, deleteResearchCacheEntry } from "../../api";
import MarkdownMessage from "../common/MarkdownMessage";

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diffDays = Math.floor((Date.now() - d) / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}

const DT_STYLE = {
  evacuation_diagram: { bg: "bg-orange-100", text: "text-orange-700", label: "Evacuation" },
  fire_safety:        { bg: "bg-red-100",    text: "text-red-700",    label: "Fire Safety" },
  structural:         { bg: "bg-blue-100",   text: "text-blue-700",   label: "Structural"  },
  electrical:         { bg: "bg-yellow-100", text: "text-yellow-700", label: "Electrical"  },
  hydraulic:          { bg: "bg-cyan-100",   text: "text-cyan-700",   label: "Hydraulic"   },
  mechanical:         { bg: "bg-purple-100", text: "text-purple-700", label: "Mechanical"  },
  architectural:      { bg: "bg-teal-100",   text: "text-teal-700",   label: "Architectural"},
};
const dtStyle = (t) => DT_STYLE[t] || { bg: "bg-slate-100", text: "text-slate-600", label: t || "Unknown" };

export default function ResearchCachePanel() {
  const [entries, setEntries]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [confirmId, setConfirmId]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getResearchCache();
      setEntries(r.data.entries || []);
    } catch {
      toast.error("Failed to load research cache");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const invalidate = async (id, e) => {
    e.stopPropagation();
    if (confirmId !== id) {
      setConfirmId(id);
      setTimeout(() => setConfirmId(null), 3000);
      return;
    }
    try {
      await deleteResearchCacheEntry(id);
      setEntries(prev => prev.filter(e => e.id !== id));
      setConfirmId(null);
      if (expandedId === id) setExpandedId(null);
      toast.success("Cache entry invalidated");
    } catch {
      toast.error("Failed to invalidate entry");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40 gap-2 text-slate-400">
        <Spinner size={18} className="animate-spin" />
        <span className="text-sm">Loading cache...</span>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48 gap-3 text-slate-400">
        <Database size={32} className="opacity-30" />
        <p className="text-sm font-medium">No cached research yet</p>
        <p className="text-xs text-center max-w-xs">
          Research results will appear here after the first evaluation.
          They are reused automatically to speed up future evaluations.
        </p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-slate-100" data-testid="research-cache-list">
      {entries.map(entry => {
        const ds        = dtStyle(entry.drawing_type);
        const isOpen    = expandedId === entry.id;
        const isConfirm = confirmId === entry.id;
        const aspects   = (entry.aspects || []).slice(0, 4);
        const hasMore   = (entry.aspects || []).length > 4;

        return (
          <div key={entry.id} data-testid={`cache-entry-${entry.id}`}>
            <div
              className="px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer group"
              onClick={() => setExpandedId(isOpen ? null : entry.id)}
            >
              <div className="flex items-start gap-3">
                <div className="flex flex-col gap-1 flex-shrink-0 pt-0.5">
                  <span className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded ${ds.bg} ${ds.text}`}>
                    {ds.label}
                  </span>
                  {entry.state && (
                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded bg-slate-100 text-slate-600">
                      {entry.state}{entry.city ? ` · ${entry.city}` : ""}
                    </span>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">
                    {entry.query_description || `${entry.drawing_type} / ${entry.state}`}
                  </p>
                  {aspects.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {aspects.map((a, i) => (
                        <span key={i} className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">{a}</span>
                      ))}
                      {hasMore && (
                        <span className="text-xs text-slate-400 px-1 py-0.5">+{entry.aspects.length - 4} more</span>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-3 flex-shrink-0" onClick={e => e.stopPropagation()}>
                  <div className="text-center">
                    <span className={`text-base font-bold ${entry.use_count > 0 ? "text-primary" : "text-slate-300"}`}>
                      {entry.use_count || 0}
                    </span>
                    <p className="text-xs text-slate-400 leading-none">uses</p>
                  </div>
                  <div className="text-right hidden sm:block">
                    <p className="text-xs text-slate-400">{formatDate(entry.created_at)}</p>
                    {entry.last_used_at && (
                      <p className="text-xs text-slate-300">used {formatDate(entry.last_used_at)}</p>
                    )}
                  </div>
                  {isConfirm ? (
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-red-600 font-medium">Delete?</span>
                      <button onClick={e => invalidate(entry.id, e)} className="p-1 text-red-600 hover:bg-red-50 rounded" title="Confirm" data-testid={`confirm-invalidate-${entry.id}`}>
                        <Check size={13} weight="bold" />
                      </button>
                      <button onClick={e => { e.stopPropagation(); setConfirmId(null); }} className="p-1 text-slate-400 hover:bg-slate-100 rounded" title="Cancel">
                        <X size={13} weight="bold" />
                      </button>
                    </div>
                  ) : (
                    <button onClick={e => invalidate(entry.id, e)} className="p-1.5 text-slate-300 hover:text-red-600 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-all" title="Invalidate this cache entry" data-testid={`invalidate-btn-${entry.id}`}>
                      <Trash size={13} />
                    </button>
                  )}
                  <button onClick={() => setExpandedId(isOpen ? null : entry.id)} className="p-1 text-slate-300 hover:text-slate-600 rounded transition-colors" data-testid={`expand-cache-${entry.id}`}>
                    {isOpen ? <CaretUp size={13} /> : <CaretDown size={13} />}
                  </button>
                </div>
              </div>
            </div>

            {isOpen && (
              <div className="px-4 pb-4 bg-slate-50 border-t border-slate-100">
                <div className="pt-3 space-y-3">
                  <div>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Originally researched</p>
                    <p className="text-sm text-slate-700 bg-white border border-slate-200 rounded px-3 py-2">{entry.query_description}</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Cached research output</p>
                    <div className="bg-white border border-slate-200 rounded px-3 py-3 max-h-72 overflow-y-auto">
                      <MarkdownMessage content={entry.research_output} className="text-xs" />
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 italic">
                    Cached {formatDate(entry.created_at)}
                    {entry.use_count > 0 && ` · reused ${entry.use_count} time${entry.use_count !== 1 ? "s" : ""}`}
                    {entry.last_used_at && ` · last used ${formatDate(entry.last_used_at)}`}
                  </p>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
